import React, { useState } from "react";
import dayjs from "dayjs";
import { Button, Input, Tag, Modal } from "antd";
import styled from "styled-components";
import {
  ProductQuestionItem,
  updateProductQuestion,
} from "api/ProductQuestions";

const Wrap = styled.div<{ processed?: boolean }>`
  margin-bottom: 20px;
  padding: 16px 20px;
  border-radius: 8px;
  background: ${(p) =>
    p.processed ? "rgba(58, 175, 169, 0.06)" : "#fff"};
  box-shadow: rgba(30, 31, 33, 0.08) 0px 2px 12px;
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #666;
  margin-bottom: 6px;
`;

const MetaItem = styled.span`
  white-space: nowrap;
`;

const ProductRow = styled.div`
  font-size: 13px;
  color: #444;
  margin-bottom: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
`;

const ArticleTag = styled.span`
  font-family: monospace;
  background: #f0f0f0;
  border-radius: 4px;
  padding: 1px 6px;
  font-size: 12px;
  color: #333;
  cursor: default;
`;

const ProductName = styled.span`
  color: #555;
`;

const AnswerMeta = styled.div`
  font-size: 12px;
  color: #888;
  margin-top: 4px;
`;

const QuestionText = styled.div`
  font-size: 15px;
  margin: 12px 0;
  white-space: pre-wrap;
`;

const AnswerBlock = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
`;

const Actions = styled.div`
  margin-top: 16px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const statusLabel = (item: ProductQuestionItem) => {
  if (item.is_processed) return "Обработано";
  return "Не обработано";
};

const fmtDate = (iso: string | null | undefined) =>
  iso ? dayjs(iso).format("DD.MM.YYYY HH:mm") : null;

export const QuestionItem = (props: {
  data: ProductQuestionItem;
  onUpdated: () => void;
}) => {
  const { data } = props;
  const [modalOpen, setModalOpen] = useState(false);
  const [text, setText] = useState(data.answer?.text || "");
  const [loading, setLoading] = useState(false);

  const saveDraft = async () => {
    setLoading(true);
    try {
      await updateProductQuestion({
        id: data.id,
        answer: { text },
        workflow_status: "AWAITING_APPROVAL",
      });
      setModalOpen(false);
      props.onUpdated();
    } finally {
      setLoading(false);
    }
  };

  const approveOnly = async () => {
    setLoading(true);
    try {
      await updateProductQuestion({
        id: data.id,
        answer: { text },
        workflow_status: "APPROVED",
      });
      setModalOpen(false);
      props.onUpdated();
    } finally {
      setLoading(false);
    }
  };

  const approveAndSend = async () => {
    setLoading(true);
    try {
      await updateProductQuestion({
        id: data.id,
        answer: { text },
        workflow_status: "APPROVED",
      });
      setModalOpen(false);
      props.onUpdated();
    } finally {
      setLoading(false);
    }
  };

  const answerCreatedAt = fmtDate(data.answer?.created_at);
  const answerSentAt = fmtDate(data.answer?.sent_at);

  return (
    <Wrap processed={data.is_processed}>
      {/* Строка 1: дата вопроса, маркетплейс, интеграция */}
      <MetaRow>
        <MetaItem>{dayjs(data.asked_at).format("DD.MM.YYYY HH:mm")}</MetaItem>
        <MetaItem>·</MetaItem>
        <Tag style={{ margin: 0 }}>{data.marketplace}</Tag>
        <MetaItem>·</MetaItem>
        <MetaItem>{data.integration_login}</MetaItem>
      </MetaRow>

      {/* Строка 2: артикул + название товара */}
      {(data.article || data.sku || data.product_name) && (
        <ProductRow>
          {(data.article || data.sku) && (
            <ArticleTag>{data.article || data.sku}</ArticleTag>
          )}
          {data.product_name && <ProductName>{data.product_name}</ProductName>}
        </ProductRow>
      )}

      <Tag color={data.is_processed ? "green" : "default"}>
        {statusLabel(data)} ({data.workflow_status})
      </Tag>

      <QuestionText>{data.question_text}</QuestionText>

      <AnswerBlock>
        <strong>Ответ: </strong>
        {data.answer?.text ? (
          <span>{data.answer.text}</span>
        ) : (
          <span style={{ color: "#999" }}>—</span>
        )}
        {/* Даты ответа */}
        {(answerCreatedAt || answerSentAt) && (
          <AnswerMeta>
            {answerCreatedAt && <span>Создан: {answerCreatedAt}</span>}
            {answerCreatedAt && answerSentAt && <span> · </span>}
            {answerSentAt && <span>Отправлен: {answerSentAt}</span>}
          </AnswerMeta>
        )}
        {data.answer?.error && (
          <div style={{ color: "crimson", marginTop: 8 }}>
            {data.answer.error}
          </div>
        )}
      </AnswerBlock>

      {!data.is_processed && (
        <Button
          type="link"
          onClick={() => setModalOpen(true)}
          style={{ paddingLeft: 0 }}
        >
          Редактировать ответ
        </Button>
      )}

      <Modal
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        title="Ответ на вопрос"
        footer={null}
        width={560}
      >
        <Input.TextArea
          rows={8}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />
        <Actions>
          <Button onClick={saveDraft} loading={loading}>
            Сохранить черновик
          </Button>
          <Button onClick={approveOnly} loading={loading}>
            Подтвердить
          </Button>
          <Button type="primary" onClick={approveAndSend} loading={loading}>
            Подтвердить и отправить
          </Button>
        </Actions>
      </Modal>
    </Wrap>
  );
};
